/**
 * The per-workflow detail surface — a PURE layout shell (props in, JSX out).
 * No Convex hooks, no navigation: the `$specId.tsx` route wires `useSpecById`,
 * tab navigation, and the pane CONTENT through props (workflows-dedicated-ui
 * §3.3, re-scoped 2026-07-16 to the lean embed path).
 *
 * Two-pane shell ported from the `WorkflowOpenMockup` visual:
 *  - LEFT pane = `chatSlot` — the route mounts the real `ChatWindow` bound to
 *    the spec's thread (falls back to a placeholder when absent, e.g. tests).
 *  - RIGHT pane = workflow header (title + `StatusPill` via
 *    `deriveSpecActivityLabel`) + an Overview · Activity segmented control.
 *    The route passes the tab bodies as slots: `overviewSlot` embeds the
 *    existing `SpecTab`, `activitySlot` the existing `ExecutionTab` — the
 *    same thread-keyed components the chat workbench uses, so both surfaces
 *    render workflow state from one implementation (panel-until-parity).
 *
 * Slot children may render `absolute inset-0` (both embedded tabs do), so the
 * tab body wrapper is `relative`.
 *
 * Mobile `<lg`: the two panes collapse to a single column with a top-level
 * Chat | Workflow toggle (chat lives behind a tab). All `dark:` variants are
 * preserved verbatim from the mockup.
 */

import { useState, type ReactNode } from "react";

import { StatusPill } from "@/components/dashboard/IntegrationCardShell";
import { deriveSpecActivityLabel } from "@domain/praxis-specs";

export type WorkflowTab = "overview" | "activity";

/** Segmented-control ordering + display labels for the detail tabs. */
const TABS: readonly { id: WorkflowTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "activity", label: "Activity" },
];

/** The minimal spec projection the detail header renders — a subset of `Doc<"specs">`. */
export interface WorkflowDetailSpec {
  readonly _id: string;
  readonly title: string;
  readonly active?: boolean;
  readonly userPaused?: boolean;
}

/**
 * Pause/Resume toggle wiring (workflow-pause-toggle spec) — the route
 * passes the relay mechanics from `useWorkflowPauseToggle`; this shell
 * stays pure and only derives WHICH affordance shows from the spec's
 * three-state label.
 */
export interface WorkflowPauseControl {
  /** A pause/activate relay is in flight — button disabled + "…" state. */
  readonly pending: boolean;
  /** Inline failure copy (relay failed / mutation threw), or null. */
  readonly error?: string | null;
  readonly onPause: () => void;
  readonly onResume: () => void;
}

export interface WorkflowDetailProps {
  readonly spec: WorkflowDetailSpec;
  /** The active detail tab (mirrors the route's `tab` search param). */
  readonly activeTab: WorkflowTab;
  /** Fired when the segmented control selects a tab. Navigation lives in the route. */
  readonly onTabChange: (tab: WorkflowTab) => void;
  /** Fired by the "Back to workflows" affordance. Navigation lives in the route. */
  readonly onBack?: () => void;
  /** Pause/Resume toggle wiring — omitted (tests) renders pill-only. */
  readonly pauseControl?: WorkflowPauseControl;
  /** LEFT pane content — the route mounts the real `ChatWindow` here. */
  readonly chatSlot?: ReactNode;
  /** Overview tab body — the route embeds `SpecTab` here. */
  readonly overviewSlot?: ReactNode;
  /** Activity tab body — the route embeds `ExecutionTab` here. */
  readonly activitySlot?: ReactNode;
}

/** Verbatim reuse of validate's unlock copy — one voice (integration contract). */
const CHECK_IT_HINT = "Check the workflow — publishing unlocks once it passes.";

/** Which mobile pane is showing under `<lg` (desktop shows both). */
type MobilePane = "chat" | "workflow";

/** Fallback left-pane content when no `chatSlot` is provided (tests). */
function ChatSlotFallback() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-coral/15 text-[15px]">
        🦞
      </span>
      <p className="font-body text-[13px] text-home-ink/45 dark:text-white/45">
        No chat for this workflow.
      </p>
    </div>
  );
}

/** The Overview · Activity segmented control, bound to `activeTab`. */
function TabControl({
  activeTab,
  onTabChange,
}: {
  activeTab: WorkflowTab;
  onTabChange: (tab: WorkflowTab) => void;
}) {
  return (
    <div className="inline-flex shrink-0 gap-0.5 rounded-full bg-home-ink/[0.05] dark:bg-white/[0.06] p-0.5">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onTabChange(t.id)}
          aria-pressed={t.id === activeTab}
          className={`rounded-full px-3 py-1 font-body text-[12px] font-semibold transition-colors ${
            t.id === activeTab
              ? "bg-white dark:bg-surface-dark-card text-home-ink dark:text-white shadow-[0_2px_8px_-4px_rgba(33,31,28,0.35)]"
              : "text-home-ink/55 dark:text-white/55"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The three-state pill + Pause/Resume toggle. Button shows only when
 * toggleable: Paused → Resume; Live → Pause; Needs check → no button,
 * the pill carries validate's Check-it hint instead (title tooltip).
 */
function PauseToggle({
  activityLabel,
  pauseControl,
}: {
  activityLabel: ReturnType<typeof deriveSpecActivityLabel>;
  pauseControl?: WorkflowPauseControl;
}) {
  const pill =
    activityLabel === "Live" ? (
      <StatusPill variant="active">Live</StatusPill>
    ) : activityLabel === "Paused" ? (
      <StatusPill variant="muted">Paused</StatusPill>
    ) : (
      <span title={CHECK_IT_HINT}>
        <StatusPill variant="muted">Needs check</StatusPill>
      </span>
    );
  if (pauseControl === undefined || activityLabel === "Needs check") {
    return pill;
  }
  const pending = pauseControl.pending;
  const isPaused = activityLabel === "Paused";
  return (
    <>
      {pill}
      <button
        type="button"
        disabled={pending}
        onClick={isPaused ? pauseControl.onResume : pauseControl.onPause}
        className="shrink-0 rounded-full border border-home-ink/12 dark:border-white/15 px-2.5 py-0.5 font-body text-[11px] font-semibold text-home-ink/65 dark:text-white/65 transition-colors hover:text-home-ink dark:hover:text-white disabled:cursor-default disabled:opacity-50"
      >
        {pending ? "Working…" : isPaused ? "Resume" : "Pause"}
      </button>
      {pauseControl.error != null ? (
        <span className="truncate font-body text-[11px] text-accent-red">
          {pauseControl.error}
        </span>
      ) : null}
    </>
  );
}

/** RIGHT pane — the workflow header + the active tab's slot content. */
function WorkflowPane({
  spec,
  activeTab,
  onTabChange,
  pauseControl,
  overviewSlot,
  activitySlot,
}: {
  spec: WorkflowDetailSpec;
  activeTab: WorkflowTab;
  onTabChange: (tab: WorkflowTab) => void;
  pauseControl?: WorkflowPauseControl;
  overviewSlot?: ReactNode;
  activitySlot?: ReactNode;
}) {
  const activityLabel = deriveSpecActivityLabel({
    active: spec.active,
    userPaused: spec.userPaused,
  });
  const body = activeTab === "overview" ? overviewSlot : activitySlot;
  return (
    <div className="flex h-full flex-col bg-home-cream-deep/40 dark:bg-surface-dark-raised">
      <div className="flex items-center justify-between gap-2 border-b border-home-ink/8 dark:border-white/8 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <h3 className="truncate font-display text-[15px] font-semibold text-home-ink dark:text-white">
            {spec.title}
          </h3>
          <PauseToggle
            activityLabel={activityLabel}
            pauseControl={pauseControl}
          />
        </div>
        <TabControl activeTab={activeTab} onTabChange={onTabChange} />
      </div>

      {/* Tab body — `relative` because the embedded SpecTab / ExecutionTab
          render `absolute inset-0`. */}
      <div className="relative min-h-0 flex-1">
        {body ?? (
          <div className="flex h-full items-center justify-center p-6">
            <p className="font-body text-[13px] text-home-ink/40 dark:text-white/40">
              {TABS.find((t) => t.id === activeTab)?.label} — nothing here yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function WorkflowDetail({
  spec,
  activeTab,
  onTabChange,
  onBack,
  pauseControl,
  chatSlot,
  overviewSlot,
  activitySlot,
}: WorkflowDetailProps) {
  const [mobilePane, setMobilePane] = useState<MobilePane>("workflow");

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] min-h-[460px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-home-ink/10 dark:border-white/10 bg-home-cream dark:bg-surface-dark-card max-lg:h-[calc(100dvh-11rem)] 2xl:h-[calc(100vh-4rem)] 2xl:max-w-none">
      {/* Top bar: "← Workflows" back affordance (all breakpoints) + the
          mobile-only Chat | Workflow pane toggle. Without `onBack` (tests)
          the bar only exists below `lg`, where the toggle still matters. */}
      <div
        className={`flex items-center gap-2 border-b border-home-ink/8 dark:border-white/8 px-4 py-2.5 ${
          onBack ? "" : "lg:hidden"
        }`}
      >
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="mr-1 font-body text-[12px] font-semibold text-home-ink/55 dark:text-white/55 transition-colors hover:text-home-ink dark:hover:text-white"
          >
            ← Workflows
          </button>
        ) : null}
        <div className="ml-auto inline-flex gap-0.5 rounded-full bg-home-ink/[0.05] dark:bg-white/[0.06] p-0.5 lg:hidden">
          {(["chat", "workflow"] as const).map((pane) => (
            <button
              key={pane}
              type="button"
              onClick={() => setMobilePane(pane)}
              aria-pressed={pane === mobilePane}
              className={`rounded-full px-3 py-1 font-body text-[12px] font-semibold capitalize transition-colors ${
                pane === mobilePane
                  ? "bg-white dark:bg-surface-dark-card text-home-ink dark:text-white shadow-[0_2px_8px_-4px_rgba(33,31,28,0.35)]"
                  : "text-home-ink/55 dark:text-white/55"
              }`}
            >
              {pane}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Chat pane: fixed 40% on `lg`+, single-column when mobile toggle=chat. */}
        <div
          className={`flex-col border-home-ink/8 dark:border-white/8 bg-home-cream dark:bg-surface-dark-card lg:flex lg:w-[40%] lg:border-r ${
            mobilePane === "chat" ? "flex flex-1" : "hidden"
          }`}
        >
          {chatSlot ?? <ChatSlotFallback />}
        </div>

        {/* Workflow pane: fills the rest on `lg`+, single-column when toggle=workflow. */}
        <div
          className={`flex-1 flex-col lg:flex ${
            mobilePane === "workflow" ? "flex" : "hidden"
          }`}
        >
          <WorkflowPane
            spec={spec}
            activeTab={activeTab}
            onTabChange={onTabChange}
            pauseControl={pauseControl}
            overviewSlot={overviewSlot}
            activitySlot={activitySlot}
          />
        </div>
      </div>
    </div>
  );
}
